import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { currencyFormatter as currencyFormatterJs } from '../QuotePage/content/QuoteGrids/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from './context/PolicyPageContext';
import { uniqueId } from '../../ui/utils';
import { ActionButton, ActionRow, Grid, GridGroup, type Row } from './tabParts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteGrids/utils.js formats an AG Grid value
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;
type PolicyPageState = { endorsements?: Row[]; setHighValueVehicles: (rows: Row[]) => void; setEndorsements: (endorsements: Row[]) => void };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: AG Grid params and API
type GridParams = any;

const HIGH_VALUE_VEHICLE = 'highValueVehicle';
const FILL_HANDLE_CELL_SELECTION = { handle: { mode: 'fill' } };
const FIELD_NAMES = ['locationAddress', 'vehicleValue', 'vehicleDescription', 'vin', 'effectiveAt', 'effectiveUntil'];

const createNewRow = () => ({
  id: uniqueId(),
  locationAddress: '',
  vehicleDescription: '',
  vin: '',
  vehicleValue: 0,
  effectiveAt: '',
  effectiveUntil: '',
});

const parseClipboardData = (text: string) =>
  !text
    ? []
    : text
        .split('\n')
        .map((row) => row.split('\t').map((cell) => cell.trim()))
        .filter((row) => row.some((cell) => cell !== ''));

const hasInvalidDateRange = (row: Row) => {
  if (!row.effectiveAt || !row.effectiveUntil) return false;
  return new Date(row.effectiveAt) >= new Date(row.effectiveUntil);
};

const hasOverlappingVIN = (row: Row, allRows: Row[]) => {
  if (!row.vin || !row.effectiveAt || !row.effectiveUntil) return false;
  const start1 = new Date(row.effectiveAt);
  const end1 = new Date(row.effectiveUntil);
  return allRows.some((other) => {
    if (other === row) return false;
    if (other.vin !== row.vin) return false;
    if (!other.effectiveAt || !other.effectiveUntil) return false;
    return start1 < new Date(other.effectiveUntil) && new Date(other.effectiveAt) < end1;
  });
};

const dateSetter = (field: 'effectiveAt' | 'effectiveUntil') => (params: GridParams) => {
  let dateValue = params.newValue;
  if (params.newValue instanceof Date) {
    const year = params.newValue.getFullYear();
    const month = String(params.newValue.getMonth() + 1).padStart(2, '0');
    const day = String(params.newValue.getDate()).padStart(2, '0');
    dateValue = `${year}-${month}-${day}`;
  }
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    params.data[field] = dateValue;
    return true;
  }
  return false;
};

const sanitizeCellValue = (value: unknown) => (typeof value === 'string' ? value.replace(/\t/g, ' ').trim() : value);

/** High-value vehicles: the exposure limit and the vehicles, pasted, edited, saved and downloaded. */
export const HighValueVehicles = forwardRef<unknown, { policy: Row; setGridReference: (ref: unknown) => void }>(({ policy, setGridReference }) => {
  const { insertMultipleEndorsements, downloadEndorsement, getEndorsements } = useApi();
  const { endorsements = [], setHighValueVehicles, setEndorsements } = usePolicyPageContext();

  const [exposureLimit, setExposureLimit] = useState(0);
  const [rows, setRows] = useState<Row[]>([]);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const gridRef = useRef<GridParams>(null);

  useEffect(() => {
    setExposureLimit(policy?.occurrenceLimit);
  }, [policy]);

  useEffect(() => {
    const hvv = endorsements?.find(({ endorsementType }) => endorsementType === HIGH_VALUE_VEHICLE);
    if (hvv?.rows?.length) {
      const mappedRows = hvv.rows.map((row: Row) => ({ ...row, id: uniqueId() }));
      setRows(mappedRows);
      setHighValueVehicles(mappedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endorsements]);

  const handlePaste = useCallback(async (params: GridParams) => {
    try {
      const pastedRows = parseClipboardData(await navigator.clipboard.readText());
      if (!pastedRows.length) return;
      const focusedCell = params.api.getFocusedCell();
      if (!focusedCell) return;
      const startRowIndex = focusedCell.rowIndex;
      const startFieldIndex = FIELD_NAMES.indexOf(focusedCell.column.colId);
      if (startFieldIndex === -1) return;
      const existingRowCount = params.api.getDisplayedRowCount();
      const rowsNeeded = startRowIndex + pastedRows.length;
      if (rowsNeeded <= existingRowCount) return;

      const rowsToAdd: Row[] = [];
      for (let i = existingRowCount; i < rowsNeeded; i++) {
        const newRow: Row = createNewRow();
        const pastedRow = pastedRows[i - startRowIndex];
        if (pastedRow) {
          pastedRow.forEach((cellValue, colOffset) => {
            const targetFieldIndex = startFieldIndex + colOffset;
            if (targetFieldIndex < FIELD_NAMES.length) {
              const fieldName = FIELD_NAMES[targetFieldIndex] as string;
              let value: unknown = cellValue;
              if (fieldName === 'vehicleValue' && value) {
                value = parseFloat(cellValue.replace(/[^0-9.-]/g, '')) || 0;
              }
              newRow[fieldName] = value;
            }
          });
        }
        rowsToAdd.push(newRow);
      }
      params.api.applyTransaction({ add: rowsToAdd });
      setRows((prev) => [...prev, ...rowsToAdd]);
      setTimeout(() => {
        params.api.refreshCells();
      }, 0);
    } catch (error) {
      console.error('Error in handlePaste:', error);
    }
  }, []);

  const getSortedRows = () => {
    if (gridRef.current?.api) {
      const sorted: Row[] = [];
      gridRef.current.api.forEachNodeAfterFilterAndSort((node: GridParams) => sorted.push(node.data));
      return sorted;
    }
    return rows;
  };

  const columnDefs = [
    {
      headerName: 'Location Address',
      field: 'locationAddress',
      editable: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      valueFormatter: (field: GridParams) => field.value || '',
    },
    {
      headerName: 'Vehicle Value',
      field: 'vehicleValue',
      editable: true,
      valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
      dataType: 'number',
    },
    {
      headerName: 'Vehicle (Year, Make, Model)',
      field: 'vehicleDescription',
      editable: true,
      valueFormatter: (field: GridParams) => field.value || '',
    },
    {
      headerName: 'VIN',
      field: 'vin',
      editable: true,
      valueFormatter: (field: GridParams) => field.value || '',
      sortable: true,
      cellStyle: (params: GridParams) => {
        const allRows: Row[] = [];
        if (params.api) {
          params.api.forEachNodeAfterFilterAndSort((node: GridParams) => allRows.push(node.data));
        } else {
          allRows.push(...rows);
        }
        return hasOverlappingVIN(params.data, allRows) ? { backgroundColor: 'var(--cell-conflict)', fontWeight: 'bold' } : null;
      },
      comparator: (vinA: string, vinB: string, nodeA: GridParams, nodeB: GridParams) => {
        const vinComparison = vinA.localeCompare(vinB);
        if (vinComparison !== 0) return vinComparison;
        return Number(new Date(nodeA.data.effectiveAt)) - Number(new Date(nodeB.data.effectiveAt));
      },
    },
    ...(['effectiveAt', 'effectiveUntil'] as const).map((field) => ({
      headerName: field === 'effectiveAt' ? 'Effective At' : 'Effective Until',
      field,
      editable: true,
      dataType: 'string',
      cellStyle: (params: GridParams) => (hasInvalidDateRange(params.data) ? { backgroundColor: 'var(--cell-invalid)' } : null),
      cellEditorSelector: () => ({ component: 'agDateCellEditor', params: { dateFormat: 'YYYY-MM-DD' } }),
      valueSetter: dateSetter(field),
      cellDataType: 'string',
    })),
  ];

  const handleDeleteRows = () => {
    if (!selectedRows.length) {
      notifyError('Please select rows to delete.');
      return;
    }
    setRows((prev) => prev.filter((row) => !selectedRows.some((deletedRow) => deletedRow.id === row.id)));
    setSelectedRows([]);
  };

  const handleAddRow = () => {
    const newRow = createNewRow();
    let insertIndex = rows.length;
    if (gridRef.current?.api) {
      const focusedCell = gridRef.current.api.getFocusedCell();
      if (focusedCell) insertIndex = focusedCell.rowIndex + 1;
    }
    setRows((prev) => {
      const updated = [...prev];
      updated.splice(insertIndex, 0, newRow);
      return updated;
    });
  };

  const onSelectionChanged = useCallback((context: GridParams) => {
    if (context) setSelectedRows(context.api.getSelectedRows());
  }, []);

  const buildRowsForRequest = () =>
    getSortedRows()
      .filter((row) =>
        Object.entries(row).some(([key, value]) => {
          if (key === 'id') return false;
          return value !== '' && value !== null && value !== undefined && !(typeof value === 'number' && value === 0);
        })
      )
      .map((row) => ({
        locationAddress: sanitizeCellValue(row.locationAddress),
        vehicleDescription: sanitizeCellValue(row.vehicleDescription),
        vin: sanitizeCellValue(row.vin),
        vehicleValue: row.vehicleValue,
        effectiveAt: sanitizeCellValue(row.effectiveAt),
        effectiveUntil: sanitizeCellValue(row.effectiveUntil),
      }));

  const handleOnSave = async () => {
    gridRef.current?.api?.stopEditing();
    setIsLoading(true);
    const payload = {
      endorsements: [{ endorsementType: HIGH_VALUE_VEHICLE, rows: buildRowsForRequest() }],
      occurrenceLimit: exposureLimit,
    };
    try {
      const response = await insertMultipleEndorsements(policy.id, payload, HIGH_VALUE_VEHICLE);
      if (response) {
        setEndorsements(await getEndorsements(policy.id));
      }
      notifySuccess('Saved Successfully.');
    } catch (error) {
      console.error('Error saving payloads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const gridOptions = {
    enableCellChangeFlash: true,
    processCellForClipboard: (params: GridParams) => params.value || '',
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 100,
    processDataFromClipboard: (params: GridParams) => {
      const { data } = params;
      while (data.length > 0 && data[data.length - 1].every((cell: string) => cell === '')) {
        data.pop();
      }
      return data;
    },
    onPasteEnd: (params: GridParams) => handlePaste(params),
    processRangeForClipboard: (params: GridParams) => {
      const { range } = params;
      const rowStrings = [];
      for (let i = range.startRow; i <= range.endRow; i++) {
        const rowNode = params.api.getDisplayedRowAtIndex(i);
        const cellStrings: unknown[] = [];
        range.columns.forEach((column: unknown) => {
          const value = params.processCellForClipboard({
            value: params.api.getCellValue({ colKey: column, rowNode }),
            node: rowNode,
            column,
            api: params.api,
            context: params.context,
          });
          cellStrings.push(value != null ? value : '');
        });
        rowStrings.push(cellStrings.join('\t'));
      }
      return rowStrings.join('\n');
    },
  };

  const handleOnDownload = async () => {
    setIsDownloading(true);
    const payload = { endorsementType: HIGH_VALUE_VEHICLE, rows: buildRowsForRequest() };
    try {
      const response = await downloadEndorsement(policy.id, payload);
      if (response) {
        window.open(response.data.value, '_blank');
      }
    } catch (error) {
      console.error('Error saving payload:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const exposureLimitColumns = [
    {
      headerName: 'Exposure Limit',
      field: 'exposureLimit',
      editable: true,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' },
      valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
    },
  ];

  return (
    <div>
      <GridGroup>
        <div className="mb-5 h-[60px] w-full">
          <Grid
            data={[{ exposureLimit }]}
            columnDefs={exposureLimitColumns}
            defaultColDef={{ flex: 1, resizable: true }}
            setGridReference={setGridReference}
            onCellValueChanged={(params: GridParams) => {
              if (params.colDef.field === 'exposureLimit') {
                setExposureLimit(params.newValue);
              }
            }}
            floatingFilter={false}
            shouldShowPagination={false}
          />
        </div>
      </GridGroup>

      <GridGroup>
        <h3 className="m-0 mb-2.5 p-2.5 text-sm font-bold">High Value Vehicles</h3>
        <div className="grid-container max-h-[600px] min-h-[200px] overflow-y-auto">
          <Grid
            data={rows}
            columnDefs={columnDefs}
            defaultColDef={{ flex: 1, minWidth: 150, resizable: true }}
            domLayout="autoHeight"
            setGridReference={(gr: GridParams) => {
              gridRef.current = gr;
              if (setGridReference) setGridReference(gr);
            }}
            gridOptions={gridOptions}
            rowSelection="multiple"
            shouldShowPagination={false}
            columnWidth={150}
            cellSelection={FILL_HANDLE_CELL_SELECTION}
            floatingFilter={false}
            onSelectionChanged={onSelectionChanged}
            onCellValueChanged={(params: GridParams) => {
              if (!gridRef.current?.api) return;
              const field = params.colDef.field;
              setRows((prev) => prev.map((row) => (row.id === params.data.id ? { ...params.data } : row)));
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (field === 'effectiveAt' || field === 'effectiveUntil') {
                    gridRef.current.api.refreshCells({ rowNodes: [params.node], columns: ['effectiveAt', 'effectiveUntil'], force: true });
                    gridRef.current.api.refreshCells({ columns: ['vin'], force: true });
                  }
                  if (field === 'vin') {
                    gridRef.current.api.refreshCells({ columns: ['vin'], force: true });
                  }
                });
              });
            }}
          />
        </div>
        <ActionRow className="gap-1.5">
          <ActionButton size="sm" onClick={handleAddRow}>
            Add Vehicle
          </ActionButton>
          <ActionButton size="sm" loading={isDownloading} onClick={handleOnDownload}>
            Download
          </ActionButton>
          <ActionButton size="sm" tone="danger" onClick={handleDeleteRows}>
            Delete Row(s)
          </ActionButton>
        </ActionRow>
      </GridGroup>

      <ActionRow>
        <ActionButton onClick={handleOnSave} loading={isLoading}>
          Save
        </ActionButton>
      </ActionRow>
    </div>
  );
});
HighValueVehicles.displayName = 'HighValueVehicles';
